import React from 'react';
import { useGameStore } from '../../state/gameState.js';
import { NOTEBOOK_THREADS, NOTEBOOK_THREAD_IDS, threadStatus } from '../../systems/notebook.js';

// How many of the newest general (unthreaded) entries the CASE LOG shows before
// the FULL LOG toggle expands the rest.
const RECENT_COUNT = 4;

const STATUS_CLASS = {
  OPEN: '',
  PRESSED: ' prs',
  CORROBORATED: ' cor',
};

/**
 * CASE NOTES — Chip's case notebook readout in the v3 HUD left panel.
 *
 * Collapsed by default (header only). When open it lists the three briefed
 * threads; each thread is itself collapsed and opens on click to reveal its
 * own notes (briefing + evidence corroboration), so the player reads them one
 * at a time instead of a wall of simultaneous entries. Status reads as a
 * colored left rail (dim OPEN / cyan PRESSED / amber CORROBORATED), derived by
 * systems/notebook.threadStatus from evidence held and topics explored. Notes
 * not tied to a thread (the network ping, the photo, free-form asks) collect
 * under CASE LOG. Read-only; labels are instrument register, never tutorial copy.
 */
export default function CaseNotes() {
  const entries = useGameStore((s) => s.notebook.entries);
  const evidence = useGameStore((s) => s.evidence);
  const exploredTopics = useGameStore((s) => s.exhaustion.exploredTopics);
  // Whole-panel collapse (collapsed by default so the readout above keeps room).
  const [open, setOpen] = React.useState(false);
  // Per-thread expand state, so threads open one at a time on demand.
  const [openThreads, setOpenThreads] = React.useState({});
  // FULL LOG vs RECENT toggle for the unthreaded CASE LOG.
  const [logExpanded, setLogExpanded] = React.useState(false);

  const toggleThread = (id) => setOpenThreads((m) => ({ ...m, [id]: !m[id] }));

  // Group entries: thread-keyed notes file under their thread; the rest collect
  // in the general CASE LOG (newest last).
  const byThread = {};
  NOTEBOOK_THREAD_IDS.forEach((id) => { byThread[id] = []; });
  const general = [];
  entries.forEach((e) => {
    if (e.threadId && byThread[e.threadId]) byThread[e.threadId].push(e);
    else general.push(e);
  });
  const shownGeneral = logExpanded ? general : general.slice(-RECENT_COUNT);
  const generalOverflow = general.length > RECENT_COUNT;

  return (
    <div className={'lnb' + (open ? ' open' : '')}>
      <button
        type="button"
        className="lp-hdr lnb-hdr"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <span>CASE NOTES</span>
        <span className="lnb-cv">{open ? '▾' : '▸'}</span>
      </button>
      {open && (
      <div className="lp-b lnb-b">
        <div className="lmicro mut">THREADS</div>
        {NOTEBOOK_THREAD_IDS.map((threadId) => {
          const status = threadStatus(threadId, { evidence, exploredTopics });
          const isOpen = !!openThreads[threadId];
          const notes = byThread[threadId];
          return (
            <div className={'lnb-th' + (STATUS_CLASS[status] || '')} key={threadId}>
              <button
                type="button"
                className="lnb-th-hd"
                onClick={() => toggleThread(threadId)}
                aria-expanded={isOpen}
                title={status}
              >
                <span className="lnb-tcol">
                  <span className="lnb-tt">{NOTEBOOK_THREADS[threadId].title}</span>
                  {status !== 'OPEN' && <span className="lnb-st">{status}</span>}
                </span>
                <span className="lnb-cv">{isOpen ? '▾' : '▸'}</span>
              </button>
              {isOpen && (
                <div className="lnb-th-b">
                  {notes.length > 0
                    ? notes.map((e) => <div className="lnb-e" key={e.id}>{e.text}</div>)
                    : <div className="lnb-e dim">No notes logged yet.</div>}
                </div>
              )}
            </div>
          );
        })}
        {general.length > 0 && (
          <div className="lnb-log">
            <hr className="lrule" />
            <div className="lmicro mut">CASE LOG</div>
            {shownGeneral.map((entry) => (
              <div className="lnb-e" key={entry.id}>{entry.text}</div>
            ))}
            {generalOverflow && (
              <button
                type="button"
                className="lnb-tog"
                onClick={() => setLogExpanded((v) => !v)}
                aria-expanded={logExpanded}
              >
                {logExpanded ? '◈ RECENT' : '◈ FULL LOG ' + general.length}
              </button>
            )}
          </div>
        )}
      </div>
      )}
    </div>
  );
}
